import { DockviewDefaultTab, type IDockviewPanelHeaderProps } from "dockview"
import { IconButton, Menu, MenuItem } from "@mui/material"
import { MoreVert } from "@mui/icons-material"
import { useState, useRef, useEffect, useCallback, type JSX, type MouseEvent, type KeyboardEvent } from "react"
import type { TabIconComponent } from "./types"
import "./custom-tab.scss"

interface CustomTabComponentProps extends IDockviewPanelHeaderProps {
  removable?: boolean
  removePanel?: (panelId: string) => void
  editable?: boolean
  onUpdateTitle?: (panelId: string, newTitle: string) => void
  icon?: TabIconComponent
}

export default function CustomTabComponent(props: CustomTabComponentProps): JSX.Element {
  const {
    removable,
    removePanel,
    editable,
    onUpdateTitle,
    icon: Icon,
    api,
    ...dockviewTabProps
  } = props
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(api.title)
  const [menuAnchor, setMenuAnchor] = useState<HTMLButtonElement | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Show menu when any action is available: Rename (editable) or Delete (removable)
  const showMenu = editable || removable

  const onContextMenu = useCallback((event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault()
  }, [])

  const handleMenuOpen = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    setMenuAnchor(event.currentTarget)
  }, [])

  const handleMenuClose = useCallback(() => {
    setMenuAnchor(null)
  }, [])

  const handleDelete = useCallback((event: MouseEvent<HTMLLIElement>) => {
    event.stopPropagation()
    handleMenuClose()
    if (removePanel) {
      removePanel(api.id)
    }
  }, [removePanel, api.id, handleMenuClose])

  const handleRename = useCallback((event: MouseEvent<HTMLLIElement>) => {
    event.stopPropagation()
    handleMenuClose()
    setEditValue(api.title)
    setIsEditing(true)
  }, [api.title, handleMenuClose])

  const handleDoubleClick = useCallback((event: MouseEvent<HTMLDivElement>) => {
    if (editable && onUpdateTitle) {
      event.stopPropagation()
      setEditValue(api.title)
      setIsEditing(true)
    }
  }, [editable, onUpdateTitle, api.title])

  const handleBlur = useCallback(() => {
    if (isEditing) {
      const trimmedValue = editValue?.trim() ?? ""
      if (trimmedValue && trimmedValue !== api.title && onUpdateTitle) {
        onUpdateTitle(api.id, trimmedValue)
      }
      setIsEditing(false)
    }
  }, [isEditing, editValue, api.title, onUpdateTitle, api.id])

  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault()
      inputRef.current?.blur()
    } else if (event.key === "Escape") {
      event.preventDefault()
      setEditValue(api.title)
      setIsEditing(false)
    }
  }, [api.title])

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  // Derive edit value from api.title when not editing
  // This ensures the value stays in sync without using setState in an effect
  const displayValue = isEditing ? editValue : api.title

  if (isEditing) {
    return (
      <div className="custom-tab-wrapper custom-tab-editing">
        <input
          ref={inputRef}
          type="text"
          className="custom-tab-input"
          value={displayValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    )
  }

  return (
    <div className="custom-tab-wrapper" onDoubleClick={handleDoubleClick}>
      {Icon && (
        <span className="custom-tab-icon">
          <Icon fontSize="small" />
        </span>
      )}
      <DockviewDefaultTab
        onContextMenu={onContextMenu}
        api={api}
        {...dockviewTabProps}
        hideClose={true}
      />
      {showMenu && (
        <>
          <IconButton
            size="small"
            onClick={handleMenuOpen}
            className="custom-tab-menu-button"
          >
            <MoreVert fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            onClick={(e) => e.stopPropagation()}
          >
            <MenuItem onClick={handleRename} disabled={!editable}>Rename</MenuItem>
            <MenuItem onClick={handleDelete} disabled={!removable}>Delete</MenuItem>
          </Menu>
        </>
      )}
    </div>
  )
}
